import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../LoadingSpinner';
import {
  signIn,
  signUp,
  confirmSignUp,
  forgotPassword,
  forgotPasswordSubmit,
  signInWithAzure,
  hideAuthModals,
  clearError,
  clearSuccessMessage,
  selectAuth
} from '../../features/authSlice';
import { isOAuthEnabled } from '../../config/auth';
import authService from '../../services/authService';

const AuthPane = () => {
  const dispatch = useDispatch();
  const { 
    showSignIn, 
    showSignUp, 
    showConfirmSignUp, 
    showForgotPassword,
    showContact,
    isLoading, 
    error, 
    successMessage,
    passwordResetSent,
    pendingUsername,
    isAuthenticated
  } = useSelector(selectAuth);

  // Determine which view to show (priority order)
  const activeView = showContact ? 'contact'
    : showConfirmSignUp ? 'confirm' 
    : showForgotPassword ? 'forgot'
    : showSignUp ? 'signup'
    : showSignIn ? 'signin'
    : null;

  // State for forms
  const [signInData, setSignInData] = useState({ email: '', password: '' });
  const [signUpData, setSignUpData] = useState({ email: '', password: '', confirmPassword: '', name: '' });
  const [confirmData, setConfirmData] = useState({ email: pendingUsername || '', code: '' });
  const [forgotData, setForgotData] = useState({ email: '', code: '', newPassword: '', confirmPassword: '' });
  const [forgotStep, setForgotStep] = useState(passwordResetSent ? 2 : 1);
  const [contactData, setContactData] = useState({ fullName: '', email: '', message: '' });
  
  const [showPasswords, setShowPasswords] = useState({
    signIn: false,
    signUp: false,
    signUpConfirm: false,
    forgotNew: false,
    forgotConfirm: false
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // SECURITY: Clear all form data when view changes or modal closes
  useEffect(() => {
    // Clear all forms when switching views to prevent data leakage
    setSignInData({ email: '', password: '' });
    setSignUpData({ email: '', password: '', confirmPassword: '', name: '' });
    setConfirmData({ email: pendingUsername || '', code: '' });
    setForgotData({ email: '', code: '', newPassword: '', confirmPassword: '' });
    setContactData({ fullName: '', email: '', message: '' });
    setValidationErrors({});
    setShowPasswords({
      signIn: false,
      signUp: false,
      signUpConfirm: false,
      forgotNew: false,
      forgotConfirm: false
    });
    
    // Reset forgot password step to 1 when modal closes or view changes
    // This ensures user starts from the beginning when reopening forgot password
    if (activeView !== 'forgot') {
      setForgotStep(1);
    }
  }, [activeView, pendingUsername]);

  // SECURITY: Clear all form data when authentication succeeds
  useEffect(() => {
    if (isAuthenticated) {
      setSignInData({ email: '', password: '' });
      setSignUpData({ email: '', password: '', confirmPassword: '', name: '' });
      setConfirmData({ email: '', code: '' });
      setForgotData({ email: '', code: '', newPassword: '', confirmPassword: '' });
      setContactData({ fullName: '', email: '', message: '' });
      setValidationErrors({});
      setShowPasswords({
        signIn: false,
        signUp: false,
        signUpConfirm: false,
        forgotNew: false,
        forgotConfirm: false
      });
    }
  }, [isAuthenticated]);

  // Update confirm email when pendingUsername changes
  useEffect(() => {
    if (pendingUsername) {
      setConfirmData(prev => ({ ...prev, email: pendingUsername }));
    }
  }, [pendingUsername]);

  // Update forgot step when passwordResetSent changes
  useEffect(() => {
    if (passwordResetSent) {
      setForgotStep(2);
    }
  }, [passwordResetSent]);

  // Helper to get error message
  const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    
    if (typeof error === 'object') {
      const awsErrorMappings = {
        'NotAuthorizedException': 'Invalid email or password. Please check your credentials and try again.',
        'UserNotFoundException': 'No account found with this email address. Please check your email or sign up for a new account.',
        'UsernameExistsException': 'An account with this email already exists. Please sign in instead.',
        'UserNotConfirmedException': 'Please verify your email address before signing in. Check your inbox for a confirmation code.'
      };
      
      if (error.__type && awsErrorMappings[error.__type]) {
        return awsErrorMappings[error.__type];
      }
      if (error.message) return error.message;
      return JSON.stringify(error);
    }
    
    return String(error);
  };

  // Clear errors on input change
  const clearErrors = () => {
    if (error) dispatch(clearError());
    if (successMessage) dispatch(clearSuccessMessage());
  };

  // Validation functions
  const validateSignIn = () => {
    const errors = {};
    if (!signInData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(signInData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!signInData.password) {
      errors.password = 'Password is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSignUp = () => {
    const errors = {};
    if (!signUpData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signUpData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!signUpData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!signUpData.password) {
      errors.password = 'Password is required';
    } else if (signUpData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(signUpData.password)) {
      errors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }
    if (!signUpData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (signUpData.password !== signUpData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateConfirm = () => {
    const errors = {};
    if (!confirmData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(confirmData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!confirmData.code.trim()) {
      errors.code = 'Verification code is required';
    } else if (!/^\d{6}$/.test(confirmData.code.trim())) {
      errors.code = 'Verification code must be 6 digits';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForgotStep1 = () => {
    const errors = {};
    if (!forgotData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(forgotData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateForgotStep2 = () => {
    const errors = {};
    if (!forgotData.email.trim()) {
      errors.email = 'Email is required';
    }
    if (!forgotData.code.trim()) {
      errors.code = 'Verification code is required';
    } else if (!/^\d{6}$/.test(forgotData.code.trim())) {
      errors.code = 'Verification code must be 6 digits';
    }
    if (!forgotData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (forgotData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(forgotData.newPassword)) {
      errors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }
    if (!forgotData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (forgotData.newPassword !== forgotData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to check if email is a Labb employee email
  const isLabbEmail = (email) => {
    const labbDomains = ['@labb.ltd', '@labbconsulting.com', '@labb.ai'];
    return labbDomains.some(domain => email.toLowerCase().endsWith(domain));
  };

  // Submit handlers
  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    if (!validateSignIn()) return;
    
    // Check if user is trying to sign in with a Labb email - redirect to Azure OAuth
    if (isLabbEmail(signInData.email.trim())) {
      dispatch(signInWithAzure());
      return;
    }
    
    dispatch(signIn({
      username: signInData.email.trim(),
      password: signInData.password
    }));
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    if (!validateSignUp()) return;
    
    // Check if user is trying to sign up with a Labb email - redirect to Azure OAuth
    if (isLabbEmail(signUpData.email.trim())) {
      dispatch(signInWithAzure());
      return;
    }
    
    dispatch(signUp({
      username: signUpData.email.trim(),
      password: signUpData.password,
      email: signUpData.email.trim(),
      attributes: {
        name: signUpData.name.trim()
      }
    }));
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();
    if (!validateConfirm()) return;
    
    dispatch(confirmSignUp({
      username: confirmData.email.trim(),
      code: confirmData.code.trim()
    }));
  };

  const handleForgotStep1Submit = async (e) => {
    e.preventDefault();
    if (!validateForgotStep1()) return;
    
    // Check if user is trying to reset password with a Labb email - redirect to Azure OAuth
    if (isLabbEmail(forgotData.email.trim())) {
      console.log('🔀 Labb email detected, redirecting to Azure OAuth');
      dispatch(signInWithAzure());
      return;
    }
    
    console.log('📧 Requesting password reset code for:', forgotData.email.trim());
    
    const result = await dispatch(forgotPassword({
      username: forgotData.email.trim()
    }));
    
    console.log('Password reset result:', result);
    
    if (result.type === forgotPassword.fulfilled.type) {
      console.log('✅ Password reset code requested successfully');
      console.log('📧 Check email for verification code');
      setForgotStep(2);
    } else {
      console.error('❌ Password reset request failed:', result);
      console.error('Error payload:', result.payload);
    }
  };

  const handleForgotStep2Submit = async (e) => {
    e.preventDefault();
    if (!validateForgotStep2()) return;
    
    console.log('🔐 Submitting password reset confirmation');
    console.log('Email:', forgotData.email.trim());
    console.log('Code length:', forgotData.code.trim().length);
    
    const result = await dispatch(forgotPasswordSubmit({
      username: forgotData.email.trim(),
      code: forgotData.code.trim(),
      newPassword: forgotData.newPassword
    }));
    
    if (result.type === forgotPasswordSubmit.fulfilled.type) {
      console.log('✅ Password reset confirmed successfully');
    } else {
      console.error('❌ Password reset confirmation failed:', result);
      console.error('Error payload:', result.payload);
    }
  };

  const handleResendCode = async () => {
    if (!confirmData.email.trim()) return;
    
    setIsResending(true);
    setResendSuccess(false);
    
    try {
      const result = await authService.resendConfirmationCode(confirmData.email.trim());
      if (result.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error resending code:', error);
    } finally {
      setIsResending(false);
    }
  };

  // If no view is active, don't render anything
  if (!activeView) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[9999]"
      style={{ fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Left Backdrop - NOT clickable (users must use X button to close) */}
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Right Pane - 49% width on desktop, full width on mobile */}
      <motion.div
        className="absolute right-0 top-0 bottom-0 w-full md:w-[49%] bg-[#1C2837] overflow-y-auto"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          background: `
            radial-gradient(circle at 80% 20%, rgba(68, 194, 253, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 20% 80%, rgba(35, 166, 213, 0.06) 0%, transparent 50%),
            linear-gradient(180deg, #1C2837 0%, #1C2837 100%)
          `,
        }}
      >
        <div className="min-h-full flex flex-col">
          {/* Header with Close Button */}
          <div className="border-b border-gray-700/30 p-8">
            <div className="max-w-2xl mx-auto flex items-center justify-between">
              <img 
                src="/images/cara-white.png" 
                alt="Cara" 
                className="h-20 w-auto brightness-110"
              />
              <button
                onClick={() => dispatch(hideAuthModals())}
                className="text-white hover:text-[#44C2FD] transition-colors p-2 rounded-full hover:bg-white/10"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-2xl">
              <AnimatePresence mode="wait">
                {/* Sign In View */}
                {activeView === 'signin' && (
                  <motion.div
                    key="signin"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2">Sign In</h2>
                    <p className="text-gray-300 mb-8">
                      Welcome back! Please enter your credentials to access your account.
                    </p>

                    {successMessage && (
                      <motion.div
                        className="bg-green-900/30 border border-green-500/50 text-green-200 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {successMessage}
                      </motion.div>
                    )}

                    {error && (
                      <motion.div
                        className="bg-red-900/30 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {getErrorMessage(error)}
                      </motion.div>
                    )}

                    <form onSubmit={handleSignInSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={signInData.email}
                          onChange={(e) => {
                            setSignInData({ ...signInData, email: e.target.value });
                            setValidationErrors({ ...validationErrors, email: '' });
                            clearErrors();
                          }}
                          className={`w-full px-4 py-3 bg-[#0f1620] border ${
                            validationErrors.email ? 'border-red-500' : 'border-gray-600'
                          } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent`}
                          placeholder="you@example.com"
                        />
                        {validationErrors.email && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.signIn ? 'text' : 'password'}
                            name="password"
                            value={signInData.password}
                            onChange={(e) => {
                              setSignInData({ ...signInData, password: e.target.value });
                              setValidationErrors({ ...validationErrors, password: '' });
                              clearErrors();
                            }}
                            className={`w-full px-4 py-3 bg-[#0f1620] border ${
                              validationErrors.password ? 'border-red-500' : 'border-gray-600'
                            } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent pr-12`}
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, signIn: !showPasswords.signIn })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                          >
                            {showPasswords.signIn ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {validationErrors.password && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.password}</p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center uppercase tracking-wide"
                      >
                        {isLoading ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Signing in...</span>
                          </>
                        ) : (
                          'Sign In'
                        )}
                      </button>

                      <div className="text-center space-y-4 mt-6">
                        <button
                          type="button"
                          onClick={() => {
                            dispatch({ type: 'auth/showForgotPasswordModal' });
                            setValidationErrors({});
                            clearErrors();
                          }}
                          className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors text-base font-medium"
                        >
                          Forgot your password?
                        </button>
                        
                        <div className="space-y-2 text-xs text-gray-400 pt-2">
                          <div>
                            Don't have an account?{' '}
                            <button
                              type="button"
                              onClick={() => {
                                dispatch({ type: 'auth/showSignUpModal' });
                                setValidationErrors({});
                                clearErrors();
                              }}
                              className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors font-medium"
                            >
                              Sign up
                            </button>
                          </div>
                          <div>
                            Have a verification code?{' '}
                            <button
                              type="button"
                              onClick={() => {
                                dispatch({ type: 'auth/showConfirmSignUpModal' });
                                setValidationErrors({});
                                clearErrors();
                              }}
                              className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors font-medium"
                            >
                              Enter code
                            </button>
                          </div>
                          {isOAuthEnabled() && (
                            <div>
                              Work for Labb?{' '}
                              <button
                                type="button"
                                onClick={() => dispatch(signInWithAzure())}
                                disabled={isLoading}
                                className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Employee sign in
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Sign Up View */}
                {activeView === 'signup' && (
                  <motion.div
                    key="signup"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2">Sign Up</h2>
                    <p className="text-gray-400 mb-8">We require a few details from you to personalise your experience.</p>

                    {error && (
                      <motion.div
                        className="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {getErrorMessage(error)}
                      </motion.div>
                    )}

                    <form onSubmit={handleSignUpSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={signUpData.name}
                          onChange={(e) => {
                            setSignUpData({ ...signUpData, name: e.target.value });
                            setValidationErrors({ ...validationErrors, name: '' });
                            clearErrors();
                          }}
                          className={`w-full px-4 py-3 bg-gray-800 border ${
                            validationErrors.name ? 'border-red-500' : 'border-gray-700'
                          } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent`}
                          placeholder="John Doe"
                        />
                        {validationErrors.name && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={signUpData.email}
                          onChange={(e) => {
                            setSignUpData({ ...signUpData, email: e.target.value });
                            setValidationErrors({ ...validationErrors, email: '' });
                            clearErrors();
                          }}
                          className={`w-full px-4 py-3 bg-gray-800 border ${
                            validationErrors.email ? 'border-red-500' : 'border-gray-700'
                          } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent`}
                          placeholder="you@example.com"
                        />
                        {validationErrors.email && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.signUp ? 'text' : 'password'}
                            value={signUpData.password}
                            onChange={(e) => {
                              setSignUpData({ ...signUpData, password: e.target.value });
                              setValidationErrors({ ...validationErrors, password: '' });
                              clearErrors();
                            }}
                            className={`w-full px-4 py-3 bg-gray-800 border ${
                              validationErrors.password ? 'border-red-500' : 'border-gray-700'
                            } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent pr-12`}
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, signUp: !showPasswords.signUp })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                          >
                            {showPasswords.signUp ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {validationErrors.password && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.password}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPasswords.signUpConfirm ? 'text' : 'password'}
                            value={signUpData.confirmPassword}
                            onChange={(e) => {
                              setSignUpData({ ...signUpData, confirmPassword: e.target.value });
                              setValidationErrors({ ...validationErrors, confirmPassword: '' });
                              clearErrors();
                            }}
                            className={`w-full px-4 py-3 bg-gray-800 border ${
                              validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-700'
                            } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent pr-12`}
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, signUpConfirm: !showPasswords.signUpConfirm })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                          >
                            {showPasswords.signUpConfirm ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                        {validationErrors.confirmPassword && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.confirmPassword}</p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center uppercase tracking-wide"
                      >
                        {isLoading ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Creating account...</span>
                          </>
                        ) : (
                          'Create Account'
                        )}
                      </button>

                      <div className="text-center space-y-4 mt-6">
                        <button
                          type="button"
                          onClick={() => {
                            dispatch({ type: 'auth/showSignInModal' });
                            setValidationErrors({});
                            clearErrors();
                          }}
                          className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors text-base font-medium"
                        >
                          Have an account?
                        </button>
                        
                        <div className="space-y-2 text-xs text-gray-400 pt-2">
                          <div>
                            Already have a verification code?{' '}
                            <button
                              type="button"
                              onClick={() => {
                                dispatch({ type: 'auth/showConfirmSignUpModal' });
                                setValidationErrors({});
                                clearErrors();
                              }}
                              className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors font-medium"
                            >
                              Enter code
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Confirm Sign Up View */}
                {activeView === 'confirm' && (
                  <motion.div
                    key="confirm"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2">Email Verification</h2>
                    <p className="text-gray-400 mb-8">
                      {confirmData.email 
                        ? <>We've sent a verification code to <span className="text-white font-medium">{confirmData.email}</span></>
                        : 'Enter your email address and the verification code we sent you'
                      }
                    </p>

                    {resendSuccess && (
                      <motion.div
                        className="bg-green-900/20 border border-green-500 text-green-300 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        Verification code resent successfully!
                      </motion.div>
                    )}

                    {error && (
                      <motion.div
                        className="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {getErrorMessage(error)}
                      </motion.div>
                    )}

                    <form onSubmit={handleConfirmSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={confirmData.email}
                          onChange={(e) => {
                            setConfirmData({ ...confirmData, email: e.target.value });
                            setValidationErrors({ ...validationErrors, email: '' });
                            clearErrors();
                          }}
                          className={`w-full px-4 py-3 bg-gray-800 border ${
                            validationErrors.email ? 'border-red-500' : 'border-gray-700'
                          } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent`}
                          placeholder="you@example.com"
                        />
                        {validationErrors.email && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Verification Code
                        </label>
                        <input
                          type="text"
                          value={confirmData.code}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setConfirmData({ ...confirmData, code: value });
                            setValidationErrors({ ...validationErrors, code: '' });
                            clearErrors();
                          }}
                          className={`w-full px-4 py-3 bg-gray-800 border ${
                            validationErrors.code ? 'border-red-500' : 'border-gray-700'
                          } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent text-center text-2xl tracking-widest`}
                          placeholder="000000"
                          maxLength={6}
                        />
                        {validationErrors.code && (
                          <p className="mt-2 text-sm text-red-400">{validationErrors.code}</p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center uppercase tracking-wide"
                      >
                        {isLoading ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">Verifying...</span>
                          </>
                        ) : (
                          'Verify Email'
                        )}
                      </button>

                      <div className="text-center space-y-4 mt-6">
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={isResending}
                          className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isResending ? 'Resending code...' : 'Resend a code'}
                        </button>
                        
                        <div className="space-y-2 text-xs text-gray-400 pt-2">
                          <div>
                            Need an account?{' '}
                            <button
                              type="button"
                              onClick={() => {
                                dispatch({ type: 'auth/showSignUpModal' });
                                setValidationErrors({});
                                clearErrors();
                              }}
                              className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors font-medium"
                            >
                              Sign up
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Forgot Password View */}
                {activeView === 'forgot' && (
                  <motion.div
                    key="forgot"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {forgotStep === 1 ? (
                      <>
                        <h2 className="text-3xl font-bold text-white mb-2">Reset Password</h2>
                        <p className="text-gray-400 mb-8">We'll get your access restored right away, please confirm your email to receive a reset code</p>

                        {error && (
                          <motion.div
                            className="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {getErrorMessage(error)}
                          </motion.div>
                        )}

                        <form onSubmit={handleForgotStep1Submit} className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              value={forgotData.email}
                              onChange={(e) => {
                                setForgotData({ ...forgotData, email: e.target.value });
                                setValidationErrors({ ...validationErrors, email: '' });
                                clearErrors();
                              }}
                              className={`w-full px-4 py-3 bg-gray-800 border ${
                                validationErrors.email ? 'border-red-500' : 'border-gray-700'
                              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent`}
                              placeholder="you@example.com"
                            />
                            {validationErrors.email && (
                              <p className="mt-2 text-sm text-red-400">{validationErrors.email}</p>
                            )}
                          </div>

                          <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center uppercase tracking-wide"
                          >
                            {isLoading ? (
                              <>
                                <LoadingSpinner size="sm" />
                                <span className="ml-2">Sending code...</span>
                              </>
                            ) : (
                              'Send Reset Code'
                            )}
                          </button>

                          <div className="text-center space-y-4 mt-6">
                            <button
                              type="button"
                              onClick={() => {
                                dispatch({ type: 'auth/showSignInModal' });
                                setValidationErrors({});
                                clearErrors();
                                setForgotStep(1);
                              }}
                              className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors text-base font-medium"
                            >
                              Remembered your password?
                            </button>
                          </div>
                        </form>
                      </>
                    ) : (
                      <>
                        <h2 className="text-3xl font-bold text-white mb-2">Create New Password</h2>
                        <p className="text-gray-400 mb-8">
                          Enter the code sent to <span className="text-white font-medium">{forgotData.email}</span>
                        </p>

                        {error && (
                          <motion.div
                            className="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {getErrorMessage(error)}
                          </motion.div>
                        )}

                        <form onSubmit={handleForgotStep2Submit} className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Verification Code
                            </label>
                            <input
                              type="text"
                              value={forgotData.code}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setForgotData({ ...forgotData, code: value });
                                setValidationErrors({ ...validationErrors, code: '' });
                                clearErrors();
                              }}
                              className={`w-full px-4 py-3 bg-gray-800 border ${
                                validationErrors.code ? 'border-red-500' : 'border-gray-700'
                              } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent text-center text-2xl tracking-widest`}
                              placeholder="000000"
                              maxLength={6}
                            />
                            {validationErrors.code && (
                              <p className="mt-2 text-sm text-red-400">{validationErrors.code}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              New Password
                            </label>
                            <div className="relative">
                              <input
                                type={showPasswords.forgotNew ? 'text' : 'password'}
                                value={forgotData.newPassword}
                                onChange={(e) => {
                                  setForgotData({ ...forgotData, newPassword: e.target.value });
                                  setValidationErrors({ ...validationErrors, newPassword: '' });
                                  clearErrors();
                                }}
                                className={`w-full px-4 py-3 bg-gray-800 border ${
                                  validationErrors.newPassword ? 'border-red-500' : 'border-gray-700'
                                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent pr-12`}
                                placeholder="••••••••"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, forgotNew: !showPasswords.forgotNew })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                              >
                                {showPasswords.forgotNew ? (
                                  <EyeSlashIcon className="h-5 w-5" />
                                ) : (
                                  <EyeIcon className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            {validationErrors.newPassword && (
                              <p className="mt-2 text-sm text-red-400">{validationErrors.newPassword}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Confirm New Password
                            </label>
                            <div className="relative">
                              <input
                                type={showPasswords.forgotConfirm ? 'text' : 'password'}
                                value={forgotData.confirmPassword}
                                onChange={(e) => {
                                  setForgotData({ ...forgotData, confirmPassword: e.target.value });
                                  setValidationErrors({ ...validationErrors, confirmPassword: '' });
                                  clearErrors();
                                }}
                                className={`w-full px-4 py-3 bg-gray-800 border ${
                                  validationErrors.confirmPassword ? 'border-red-500' : 'border-gray-700'
                                } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent pr-12`}
                                placeholder="••••••••"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, forgotConfirm: !showPasswords.forgotConfirm })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                              >
                                {showPasswords.forgotConfirm ? (
                                  <EyeSlashIcon className="h-5 w-5" />
                                ) : (
                                  <EyeIcon className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                            {validationErrors.confirmPassword && (
                              <p className="mt-2 text-sm text-red-400">{validationErrors.confirmPassword}</p>
                            )}
                          </div>

                          <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center uppercase tracking-wide"
                          >
                            {isLoading ? (
                              <>
                                <LoadingSpinner size="sm" />
                                <span className="ml-2">Resetting password...</span>
                              </>
                            ) : (
                              'Reset Password'
                            )}
                          </button>
                        </form>
                      </>
                    )}
                  </motion.div>
                )}

                {/* Contact Form View */}
                {activeView === 'contact' && (
                  <motion.div
                    key="contact"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h2 className="text-3xl font-bold text-white mb-2">Contact Us</h2>
                    <p className="text-gray-300 mb-8">
                      Send us a message and we'll get back to you soon.
                    </p>

                    {successMessage && (
                      <motion.div
                        className="bg-green-900/30 border border-green-500/50 text-green-200 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {successMessage}
                      </motion.div>
                    )}

                    {error && (
                      <motion.div
                        className="bg-red-900/30 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg mb-6"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {error}
                      </motion.div>
                    )}

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        // TODO: Call API to send email
                        console.log('Contact form submitted:', contactData);
                        alert('Thank you! Your message has been sent.');
                        setContactData({ fullName: '', email: '', message: '' });
                        dispatch(hideAuthModals());
                      }}
                      className="space-y-6"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          value={contactData.fullName}
                          onChange={(e) => setContactData({ ...contactData, fullName: e.target.value })}
                          className="w-full px-4 py-3 bg-[#0f1620] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent"
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          required
                          value={contactData.email}
                          onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                          className="w-full px-4 py-3 bg-[#0f1620] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent"
                          placeholder="Enter your email address"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Message
                        </label>
                        <textarea
                          required
                          value={contactData.message}
                          onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                          rows={6}
                          className="w-full px-4 py-3 bg-[#0f1620] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent resize-vertical"
                          placeholder="Tell us about your enquiry..."
                        />
                      </div>

                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={() => dispatch(hideAuthModals())}
                          className="flex-1 bg-transparent text-white font-semibold py-3 px-6 rounded-lg border-2 border-[#44C2FD] hover:bg-[#44C2FD]/20 hover:border-[#3AB0E8] transition-all duration-300 uppercase tracking-wide relative group"
                          style={{
                            boxShadow: '0 0 20px rgba(68, 194, 253, 0.15)',
                          }}
                        >
                          <span className="relative z-10">Cancel</span>
                          <div className="absolute inset-0 bg-gradient-to-r from-[#44C2FD]/0 via-[#44C2FD]/30 to-[#44C2FD]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                        </button>
                        <button
                          type="submit"
                          className="flex-1 bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-colors uppercase tracking-wide"
                        >
                          Submit
                        </button>
                      </div>

                      <div className="text-center mt-4">
                        <button
                          type="button"
                          onClick={() => setContactData({ fullName: '', email: '', message: '' })}
                          className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors text-sm"
                        >
                          Clear
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AuthPane;
